import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  concat,
  concatMap,
  defer,
  EMPTY,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { Option } from '@sapphire/result';

import { APP_ROUTES } from '@ps/core-config';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { SupplierService, SurveyService } from '@ps/survey-data-access';
import { SURVEY_STATUS, SurveyStatus } from '@ps/survey-kernel';
import {
  launchPricingSummary,
  launchStatusErrorMessage,
  LaunchBillingContext,
} from '@ps/survey-utils';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';

import { FlexibilityPricesService } from '../../services/flexibility-prices/flexibility-prices.service';
import { selectSurveyId } from '../core/survey-creation-core.selectors';
import { SurveyCreationCoreActions } from '../core/survey-creation-core.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';

// The save half of a launch lives in SurveyCreationSaveEffects (it reuses the
// save pipeline); this class owns what happens once the survey is persisted.
@Injectable()
export class SurveyCreationLaunchSubmitEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _toast = inject(ToastService);

  private readonly _surveyService = inject(SurveyService);
  private readonly _supplierService = inject(SupplierService);
  private readonly _flexibilityPricesService = inject(FlexibilityPricesService);
  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  public readonly loadLaunchPricing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.loadLaunchPricing),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      switchMap(([, surveyId]) =>
        Option.from(surveyId).match({
          some: (id) =>
            forkJoin([
              this._flexibilityPricesService.get(id),
              this._supplierService.allocations(id, { scope: 'launch' }),
            ]).pipe(
              map(([prices, suppliers]) =>
                SurveyCreationLaunchApiActions.launchPricingLoaded({
                  pricing: launchPricingSummary(prices, suppliers),
                }),
              ),
              this.psCatchError(
                (error) =>
                  of(
                    SurveyCreationLaunchApiActions.launchPricingFailure({
                      error: error.message,
                    }),
                  ),
                { notify: true },
              ),
            ),
          none: () =>
            of(
              SurveyCreationLaunchApiActions.launchPricingFailure({
                error: 'No survey to price',
              }),
            ),
        }),
      ),
    ),
  );

  public readonly changeLaunchStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchApiActions.changeLaunchStatus),
      switchMap(({ surveyId, status, pricingConfirmationMessage }) =>
        this._logPricingConfirmation(surveyId, pricingConfirmationMessage).pipe(
          concatMap(() =>
            this._surveyService.updateSurveyStatus(surveyId, status, 'page'),
          ),
          // Swap the raw backend message for the billing-aware one before
          // psCatchError toasts it.
          catchError((error: unknown) =>
            throwError(
              () =>
                new Error(
                  launchStatusErrorMessage(error, this._billingContext()),
                ),
            ),
          ),
          tap(() => this._toast.success(launchedMessage(status))),
          switchMap(() =>
            concat(
              of(SurveyCreationLaunchApiActions.launchSurveySuccess()),
              // Reset before navigating so the dashboard never sees this
              // survey's creation state.
              of(SurveyCreationCoreActions.reset()),
              defer(() => this._navigateTo(APP_ROUTES.DASHBOARD)),
            ),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationLaunchApiActions.launchSurveyFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  private _logPricingConfirmation(
    surveyId: number,
    message: string,
  ): Observable<void> {
    return message
      ? this._surveyService.logPricingConfirmation(surveyId, message)
      : of(undefined);
  }

  private _billingContext(): LaunchBillingContext {
    return {
      rulesEnabled:
        !!this._userSettingsSelectors.buyerSettings().enableBillingNumberRules,
      example: Option.from(this._userSettingsSelectors.billingRuleRecord()).map(
        (record) => record.example,
      ),
    };
  }

  // Navigates as a side effect and contributes no action to the stream.
  private _navigateTo(route: string): Observable<never> {
    this._router.navigate([route]);
    return EMPTY;
  }
}

function launchedMessage(status: SurveyStatus): string {
  return status === SURVEY_STATUS.SCHEDULED
    ? 'Survey will be launched at the scheduled time launch'
    : 'Survey successfully launched';
}
